import React, { useEffect, useMemo, useState } from 'react';
import { useLazyQuery } from 'react-apollo';
import gql from 'graphql-tag';
import { usersVisited } from '../../../graphql/queries';
import TableUserInfo from '../UserInfo/TableUserInfo';
import '../UserInfo/UserInfo.scss';

function startOfDayISO(d = new Date()) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x.toISOString();
}
function endOfDayISO(d = new Date()) {
  const x = new Date(d);
  x.setHours(23, 59, 59, 999);
  return x.toISOString();
}

export default function Dashboard(props) {
  const loggedInUser = (props && props.user) || null;
  const [adminEmails, setAdminEmails] = useState([]);
  const [range, setRange] = useState('30d');
  const [showEngagedOnly, setShowEngagedOnly] = useState(false);
  const isLocalhost = typeof window !== 'undefined' && ['localhost', '127.0.0.1', '::1'].includes(window.location.hostname);

  useEffect(() => {
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex,nofollow';
    document.head.appendChild(meta);
    return () => {
      try { document.head.removeChild(meta); } catch (_) {}
    };
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const base = (typeof process !== 'undefined' && process.env && process.env.REACT_APP_BACKEND_URL) || '';
        const res = await fetch(`${base}/public-config`, { credentials: 'include' });
        const j = await res.json();
        if (j && j.ok && Array.isArray(j.adminEmails)) {
          setAdminEmails(j.adminEmails.map((e) => String(e).toLowerCase()));
        }
      } catch (_) {}
    })();
  }, []);
  const { from, to } = useMemo(() => {
    const now = new Date();
    if (range === 'yesterday') {
      const y = new Date(now.getTime() - 1 * 24 * 60 * 60 * 1000);
      return { from: startOfDayISO(y), to: endOfDayISO(now) };
    }
    if (range === '3d') {
      const from = new Date(now.getTime() - 3 * 24 * 60 * 60 * 1000);
      return { from: startOfDayISO(from), to: endOfDayISO(now) };
    }
    if (range === '7d') {
      const from = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      return { from: startOfDayISO(from), to: endOfDayISO(now) };
    }
    if (range === '30d') {
      const from = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
      return { from: startOfDayISO(from), to: endOfDayISO(now) };
    }
    if (range === '3m') {
      const from = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
      return { from: startOfDayISO(from), to: endOfDayISO(now) };
    }
    if (range === '6m') {
      const from = new Date(now.getTime() - 180 * 24 * 60 * 60 * 1000);
      return { from: startOfDayISO(from), to: endOfDayISO(now) };
    }
    return { from: startOfDayISO(now), to: endOfDayISO(now) };
  }, [range]);

  const [loadUsers, { data, loading }] = useLazyQuery(gql(usersVisited), {
    fetchPolicy: 'network-only',
  });

  useEffect(() => {
    loadUsers({ variables: { from, to } });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onRefresh = () => {
    loadUsers({ variables: { from, to } });
  };

  const users = useMemo(() => {
    const arr = data?.usersVisited || [];
    const withTs = arr.map((u) => {
      let ts = (Number(u.updatedAt) || new Date(u.updatedAt).getTime() || 0);
      if (!ts) {
        ts = (Number(u.createdAt) || new Date(u.createdAt).getTime() || 0);
      }
      return { u, ts };
    });
    return withTs.sort((a,b) => b.ts - a.ts).map(x => x.u);
  }, [data]);

  const isEngaged = (user) => {
    const hasProgApp = !!(user.userProgress && (
      // New flat format: { progressById: { [id]: { completed: bool } } }
      (user.userProgress.progressById &&
        Object.values(user.userProgress.progressById).some(v => v?.completed)) ||
      // Old nested format: { courses: [...] }
      (Array.isArray(user.userProgress.courses) &&
        user.userProgress.courses.some(course =>
          course.completed ||
          (Array.isArray(course.lessons) && course.lessons.some(lesson =>
            lesson.completed ||
            (Array.isArray(lesson.steps) && lesson.steps.some(step =>
              step.completed ||
              (Array.isArray(step.stepItems) && step.stepItems.some(si => si.completed))
            ))
          ))
        ))
    ));
    const hasProgFelt = Array.isArray(user.relationships) && user.relationships.length > 0;
    return hasProgApp || hasProgFelt;
  };

  const engagedUsers = useMemo(() => users.filter(isEngaged), [users]);
  const displayedUsers = showEngagedOnly ? engagedUsers : users;

  const isAdmin = !!(loggedInUser && loggedInUser.email && adminEmails.includes(String(loggedInUser.email).toLowerCase()));

  return (
    <>
      {(!isLocalhost) ? null :
      <>
        {isAdmin ? (
          <div className="userinfo-container" style={{ paddingTop: 0 }}>
            <div className="userinfo-top" style={{ position: 'relative', zIndex: 1 }}>
              <div className="userinfo-header">
                <div className="userinfo-header__title">Admin Dashboard</div>
                <div className="userinfo-refresh" onClick={onRefresh} title="Refresh">
                  <span style={{ color: 'white', fontWeight: 700 }}>↻</span>
                </div>
              </div>
              <div className="userinfo-container__email" style={{ alignItems: 'center' }}>
                <label style={{ fontSize: 14, marginRight: 8 }}>Range:</label>
                <select value={range} onChange={(e) => setRange(e.target.value)}>
                  <option value="today">Today</option>
                  <option value="yesterday">Today + Yesterday</option>
                  <option value="3d">Last 3 days</option>
                  <option value="7d">Last 7 days</option>
                  <option value="30d">Last 30 days</option>
                  <option value="3m">Last 3 months</option>
                  <option value="6m">Last 6 months</option>
                </select>
                <div className="userinfo_button noselect" style={{ marginLeft: 8 }} onClick={onRefresh}>
                  {loading ? '…' : 'Load'}
                </div>
              </div>
              {users.length > 0 && (
                <div style={{ padding: '10px 0 4px 0', fontSize: 14, display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span>{users.length} users</span>
                  <span style={{ color: '#aaa' }}>|</span>
                  <span
                    onClick={() => setShowEngagedOnly(v => !v)}
                    style={{
                      cursor: 'pointer',
                      fontWeight: 600,
                      color: showEngagedOnly ? 'var(--color-skyblue)' : 'inherit',
                      textDecoration: 'underline',
                    }}
                    title="Click to toggle engaged-only filter"
                  >
                    {engagedUsers.length} engaged
                  </span>
                  {showEngagedOnly && (
                    <span style={{ fontSize: 12, color: 'var(--color-skyblue)' }}>(showing engaged only)</span>
                  )}
                </div>
              )}
              <div className="userinfo-container__tableUserInfo">
                <TableUserInfo data={displayedUsers} linkToUserInfo adminEmails={adminEmails} />
              </div>
            </div>
            <div style={{ paddingTop: 188 }} />
          </div>
        ) : (
          <div style={{ padding: 16 }}>
            <div className="no-data">Go back to homepage</div>
            <a className="userinfo_button noselect" href="/" style={{ display: 'inline-flex', marginTop: 8 }}>Home</a>
          </div>
        )}
      </>}
    </>
  );
}


